/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import api, { TIPO_LINEA } from "@/lib/api";
import DescargarFotosCarpeta from "@/components/DescargarFotosCarpeta";
import ResetParadaDialog from "@/components/ResetParadaDialog";
import { ResumenMontajeLista, DetalleMontajeReporte } from "@/components/reportes/DetalleMontajeReporte";
import { FotosOficinaActions, contarOrigenFotos } from "@/components/reportes/FotosOficinaActions";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  CheckCircle2, XCircle, AlertTriangle, MapPin, Calendar, Loader2, ExternalLink, FileText,
  Send, Mail, Smartphone, MessageCircle, ImageIcon, ChevronRight, FileCheck2, Download, Pencil,
} from "lucide-react";
import { cloudinaryThumb } from "@/lib/cloudinary";
import PhotoLightbox from "@/components/PhotoLightbox";
import EspacioCombobox from "@/components/EspacioCombobox";
import { toast } from "sonner";
import { ImporteTrabajo, ConfirmarCeroDialog, eur } from "@/components/reportes/ImporteTrabajo";
import { RequisitosFacturacionBlock, BotonFDBloqueado, FacturacionChip } from "@/components/reportes/RequisitosFacturacion";

const CIERRE_LABEL = {
  montado:     { label: "Montado", color: "bg-emerald-500/20 text-emerald-300" },
  semi_montado:{ label: "Semi montado", color: "bg-amber-500/20 text-amber-300" },
  incidencia:  { label: "Incidencia", color: "bg-red-500/20 text-red-300" },
  cancelado:   { label: "Cancelado", color: "bg-zinc-700/40 text-zinc-300" },
  no_realizada:{ label: "No realizada", color: "bg-red-900/40 text-red-200" },
};

const TIPO_INC = {
  tiempo: "⏰ Tiempo", medida: "📐 Medida", otros: "❓ Otros",
};

function fechaCorta(iso) {
  if (!iso) return "—";
  return new Date(iso + "T00:00:00").toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" });
}

export default function Reportes() {
  const { hasRole } = useAuth();
  const esEconomicoLista = hasRole("admin") || hasRole("comercial") || hasRole("administracion");
  const [pendientes, setPendientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sel, setSel] = useState(null);
  const [showEnvio, setShowEnvio] = useState(false);
  const [showAlbaran, setShowAlbaran] = useState(false);
  const [verFinalizados, setVerFinalizados] = useState(false);
  // Iter 77 — auto-refresh para evitar que Fernando se quede con la lista
  // desactualizada mientras los montadores reportan en tiempo real.
  const [lastFetch, setLastFetch] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const POLL_MS = 20000;

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const { data } = await api.get("/reportes-pendientes", {
        params: { incluir_finalizados: verFinalizados, _t: Date.now() },
        headers: { "Cache-Control": "no-cache" },
      });
      setPendientes(data);
      setLastFetch(new Date());
      if (sel) {
        const refreshed = data.find((r) => r.ruta_id === sel.ruta_id && r.idx === sel.idx);
        setSel(refreshed || null);
      }
    } finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [verFinalizados]);
  // Iter 77 — polling silencioso cada POLL_MS si autoRefresh está activo y
  // la pestaña está visible (no refrescar a saco en background).
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const tick = () => { if (!document.hidden) load({ silent: true }); };
    const id = setInterval(tick, POLL_MS);
    const visHandler = () => { if (!document.hidden) load({ silent: true }); };
    document.addEventListener("visibilitychange", visHandler);
    // También recargar cuando se haya cambiado algo en otra pestaña (cross-tab)
    const storageHandler = (e) => {
      if (e.key === "dnf-reporte-actualizado") load({ silent: true });
    };
    window.addEventListener("storage", storageHandler);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", visHandler);
      window.removeEventListener("storage", storageHandler);
    };
    // eslint-disable-next-line
  }, [autoRefresh, verFinalizados, sel]);

  return (
    <div className="p-8 max-w-[1800px]">
      <PageHeader title="Reportes por validar"
        subtitle={
          verFinalizados
            ? `${pendientes.length} reportes (incluyendo finalizados)`
            : `${pendientes.length} reportes pendientes de revisar y enviar al cliente`
        } />

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <button
          onClick={() => setVerFinalizados(!verFinalizados)}
          data-testid="toggle-ver-finalizados"
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
            verFinalizados
              ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
              : "bg-zinc-900/40 border-zinc-700 text-zinc-400 hover:border-zinc-600"
          }`}
        >
          {verFinalizados ? "✓ Mostrando finalizados" : "Ver finalizados"}
        </button>
        {verFinalizados && (
          <span className="text-[11px] text-zinc-500">Incluye reportes ya aprobados, enviados al cliente y a FD</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {/* Iter 77 — indicador de auto-refresh + botón manual */}
          <button
            onClick={() => setAutoRefresh((v) => !v)}
            data-testid="toggle-auto-refresh"
            title={autoRefresh ? `Auto cada ${POLL_MS/1000}s` : "Auto-refresh apagado"}
            className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
              autoRefresh
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                : "bg-zinc-900/40 border-zinc-700 text-zinc-500"
            }`}>
            {autoRefresh ? (
              <span className="flex items-center gap-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                LIVE
              </span>
            ) : "PAUSADO"}
          </button>
          {lastFetch && (
            <span className="text-[10px] text-zinc-500 font-mono"
              title={lastFetch.toLocaleString("es-ES")}
              data-testid="reportes-last-fetch">
              act. {lastFetch.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          )}
          <button
            onClick={() => load({ silent: false })}
            data-testid="reportes-refresh-btn"
            disabled={refreshing || loading}
            className="text-xs px-3 py-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 disabled:opacity-50">
            {refreshing || loading ? (
              <span className="flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" />Actualizando</span>
            ) : "↻ Actualizar"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-5">
        {/* LISTA */}
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3 max-h-[calc(100vh-180px)] overflow-y-auto">
          {loading ? (
            <div className="text-center py-10 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          ) : pendientes.length === 0 ? (
            <div className="text-center py-10 text-zinc-500 text-sm italic">
              <CheckCircle2 className="w-8 h-8 text-emerald-500/40 mx-auto mb-2" /> Sin reportes pendientes
            </div>
          ) : (
            <div className="space-y-1.5" data-testid="reportes-list">
              {pendientes.map((r) => {
                const lab = CIERRE_LABEL[r.parada.estado_cierre] || CIERRE_LABEL.montado;
                const isSel = sel && sel.ruta_id === r.ruta_id && sel.idx === r.idx;
                const finalizado = (r.parada.validacion?.estado === "aprobado") && (r.parada.envios?.length > 0) && !!r.parada.fd_delivery_note_id;
                const enviadoCliente = (r.parada.envios?.length || 0) > 0;
                const enviadoAuto = ((r.parada.envios_cliente || []).length || 0) > 0;
                const albaranOk = !!r.parada.fd_delivery_note_id || !!r.parada.albaran_local_id;
                return (
                  <button key={`${r.ruta_id}-${r.idx}`} onClick={() => setSel(r)}
                    data-testid={`reporte-${r.codigo_ruta}-${r.idx}`}
                    className={`w-full text-left rounded-lg p-2.5 transition-colors border ${
                      isSel ? "bg-amber-500/10 border-amber-500/40"
                      : finalizado ? "bg-zinc-950/30 border-zinc-800 opacity-60 hover:opacity-100"
                      : "bg-zinc-950/60 border-zinc-800 hover:border-zinc-700"
                    }`}>
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span className="text-[10px] font-mono text-amber-400">{r.codigo_ruta}</span>
                      <span className="text-[10px] text-zinc-500">#{r.parada.orden}</span>
                      {r.cadena_nombre && (
                        <span data-testid={`tag-cadena-${r.ruta_id}-${r.idx}`}
                          className="text-[9px] uppercase px-1.5 py-0.5 rounded border"
                          style={{
                            color: r.cadena_color || "#a78bfa",
                            borderColor: (r.cadena_color || "#a78bfa") + "40",
                            backgroundColor: (r.cadena_color || "#a78bfa") + "1a",
                          }}>
                          {r.cadena_nombre}
                        </span>
                      )}
                      {r.provincia_principal && (
                        <span data-testid={`tag-provincia-${r.ruta_id}-${r.idx}`}
                          className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700">
                          📍 {r.provincia_principal}
                        </span>
                      )}
                      {r.peticion?.marca && (
                        <span data-testid={`tag-marca-${r.ruta_id}-${r.idx}`}
                          className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/30 font-semibold"
                          title={`Marca: ${r.peticion.marca}`}>
                          🏷 {r.peticion.marca}
                        </span>
                      )}
                      {(() => {
                        // Feb 2026 — Etiquetas de tipos de trabajo (Montaje / Retirada / Producción...)
                        // Preferimos `tipos[]` multi; si viene vacío, usamos LEGACY `tipo`.
                        const tiposLinea = (r.linea?.tipos && r.linea.tipos.length > 0)
                          ? r.linea.tipos
                          : (r.linea?.tipo ? [r.linea.tipo] : []);
                        // Mapa amigable ES para keys atómicas (montaje/retirada/diseno/produccion/envio/desmontaje)
                        const LABEL_ES = {
                          montaje: "Montaje", retirada: "Retirada", desmontaje: "Desmontaje",
                          diseno: "Diseño", produccion: "Producción", envio: "Envío",
                          solo_montaje: "Solo montaje", produccion_montaje: "Prod+Montaje",
                          diseno_produccion_montaje: "Diseño+Prod+Montaje",
                          produccion_envio: "Prod+Envío", diseno_produccion: "Diseño+Prod",
                          diseno_produccion_envio: "Diseño+Prod+Envío",
                        };
                        const COLOR_ES = {
                          montaje: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
                          retirada: "bg-orange-500/15 text-orange-300 border-orange-500/30",
                          desmontaje: "bg-orange-500/15 text-orange-300 border-orange-500/30",
                          diseno: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
                          produccion: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
                          envio: "bg-pink-500/15 text-pink-300 border-pink-500/30",
                          solo_montaje: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
                          produccion_montaje: "bg-teal-500/15 text-teal-300 border-teal-500/30",
                          diseno_produccion_montaje: "bg-blue-500/15 text-blue-300 border-blue-500/30",
                          produccion_envio: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
                          diseno_produccion: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
                          diseno_produccion_envio: "bg-pink-500/15 text-pink-300 border-pink-500/30",
                        };
                        return tiposLinea.slice(0, 3).map((t) => (
                          <span key={t}
                            data-testid={`tag-tipo-${r.ruta_id}-${r.idx}-${t}`}
                            className={`text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border ${COLOR_ES[t] || "bg-zinc-800 text-zinc-300 border-zinc-700"}`}
                            title={`Tipo de trabajo: ${LABEL_ES[t] || (TIPO_LINEA?.[t]?.label) || t}`}>
                            {LABEL_ES[t] || (TIPO_LINEA?.[t]?.label) || t}
                          </span>
                        ));
                      })()}
                      <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded ml-auto ${lab.color}`}>{lab.label}</span>
                      {(r.parada.foto_previa_omitida || r.parada.evidencia_incompleta) && (
                        <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-500/40"
                          data-testid={`tag-evidencia-${r.ruta_id}-${r.idx}`} title="Excepción de evidencia fotográfica">⚠ Evid.</span>
                      )}
                    </div>
                    <div className="text-sm font-semibold truncate">{r.espacio?.nombre || "—"}</div>
                    <div className="text-[11px] text-zinc-500 flex items-center gap-1 mt-0.5">
                      <Calendar className="w-2.5 h-2.5" /> {fechaCorta(r.fecha)}
                      {r.parada.tipo_incidencia && <span className="ml-1">· {TIPO_INC[r.parada.tipo_incidencia]}</span>}
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-0.5 truncate flex items-center gap-1.5">
                      <span className="truncate">{r.cliente_nombre}</span>
                      {esEconomicoLista && <FacturacionChip fact={r.facturacion} testId={`tag-facturacion-${r.ruta_id}-${r.idx}`} />}
                    </div>
                    <ResumenMontajeLista r={r} />
                    {/* Etiquetas binarias rojo/verde (Jun 2026 — Fernando) */}
                    <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                      <span data-testid={`tag-enviado-${r.ruta_id}-${r.idx}`}
                        className={`text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded ${
                          enviadoCliente || enviadoAuto
                            ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                            : "bg-red-500/10 text-red-300 border border-red-500/30"
                        }`}
                        title={enviadoAuto && !enviadoCliente ? "Enviado automáticamente al validar" : ""}
                      >
                        {enviadoCliente || enviadoAuto ? "✓ Cliente" : "✗ Cliente"}
                        {enviadoAuto && !enviadoCliente && <span className="ml-1 opacity-80">·auto</span>}
                      </span>
                      <span data-testid={`tag-albaran-${r.ruta_id}-${r.idx}`}
                        className={`text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded ${
                          albaranOk
                            ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                            : "bg-red-500/10 text-red-300 border border-red-500/30"
                        }`}>
                        {albaranOk ? "✓ Albarán" : "✗ Albarán"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* DETALLE */}
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 min-h-[400px]">
          {!sel ? (
            <div className="text-center py-20 text-zinc-500 text-sm">Selecciona un reporte para revisar</div>
          ) : (
            <ReporteDetalle reporte={sel} onChange={() => { load(); }}
              onEnviar={() => setShowEnvio(true)}
              onAlbaranar={() => setShowAlbaran(true)}
              onEnviarFD={async (nuevo, extra = {}) => {
                try {
                  const qs = new URLSearchParams();
                  if (nuevo) qs.set("nuevo", "true");
                  if (extra.confirmar_cero) { qs.set("confirmar_cero", "true"); if (extra.motivo_cero) qs.set("motivo_cero", extra.motivo_cero); }
                  const q = qs.toString();
                  const { data } = await api.post(`/rutas/${sel.ruta_id}/paradas/${sel.idx}/enviar-fd${q ? `?${q}` : ""}`);
                  toast.success(data.mensaje || `Albarán FD ${data.fd_number} OK`);
                  load();
                  return true;
                } catch (err) {
                  const status = err?.response?.status;
                  const detail = err?.response?.data?.detail;
                  const msg = typeof detail === "object" && detail ? detail.msg : detail;
                  if (status === 409 && detail?.code === "importe_cero") {
                    toast.warning(msg, { duration: 8000 });
                  } else if (status === 409 && (detail?.code === "requisitos_facturacion_incompletos" || detail?.code === "requisitos_facturacion_conflicto")) {
                    toast.error(msg, { duration: 9000 });
                    load();
                  } else if (status === 409) {
                    // El reporte ya está enviado a FD; recargamos para que la UI
                    // refleje el banner y oculte el botón verde.
                    toast.info(msg || "Este reporte ya fue enviado a FD", { duration: 6000 });
                    load();
                  } else {
                    toast.error(msg || "Error enviando a FD", { duration: 6000 });
                  }
                  return false;
                }
              }} />
          )}
        </div>
      </div>

      {sel && showEnvio && <DialogEnvio reporte={sel} onClose={() => setShowEnvio(false)} onDone={() => { setShowEnvio(false); load(); }} />}
      {sel && showAlbaran && <DialogAlbaran reporte={sel} onClose={() => setShowAlbaran(false)} onDone={() => { setShowAlbaran(false); load(); }} />}
    </div>
  );
}

function ReporteDetalle({ reporte, onChange, onEnviar, onAlbaranar, onEnviarFD }) {
  const { hasPermission, hasRole } = useAuth();
  const puedeResetear = hasPermission("rutas:resetear_parada");
  const esEconomico = hasRole("admin") || hasRole("comercial") || hasRole("administracion");
  const [eco, setEco] = useState(null);
  const [cero, setCero] = useState(null);
  const [soft, setSoft] = useState(null);
  const [fdLoading, setFdLoading] = useState(false);
  // Jun-2026 · requisitos de facturación del cliente (DIAGRAFOS…): «aprobado» ≠ «listo para FD»
  const [completarDatos, setCompletarDatos] = useState(false);
  const [factKey, setFactKey] = useState(0);
  const fact = eco?.facturacion;
  const fdBloqueado = !!(fact?.aplica && !fact.listo);
  const pedirFD = (nuevo) => {
    if (fdBloqueado) { toast.warning(`Faltan datos para facturar a ${fact.cliente?.nombre || "este cliente"}. Completa los datos antes de enviar a FD.`); setCompletarDatos(true); return; }
    if (eco?.total_cero) { setCero({ nuevo }); return; }
    if (eco?.aviso_montaje_cero) { setSoft({ nuevo }); return; }
    onEnviarFD && onEnviarFD(nuevo);
  };
  const onUpdated = onChange;
  const p = reporte.parada;
  const lab = CIERRE_LABEL[p.estado_cierre] || CIERRE_LABEL.montado;
  const [notas, setNotas] = useState("");
  const [saving, setSaving] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [downloadsInfo, setDownloadsInfo] = useState(null);
  // Iter 101 — completar datos de facturación de paradas manuales
  const [showCompletar, setShowCompletar] = useState(false);

  const onFinalizarReporte = async () => {
    if (!window.confirm("¿Cerrar este reporte definitivamente?\n\nSe quita de la lista de pendientes. Si te equivocas, puedes reabrirlo desde el toggle 'Ver finalizados'.")) return;
    try {
      await api.post(`/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/finalizar-reporte`);
      toast.success("🏁 Reporte finalizado");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al finalizar");
    }
  };

  const onReabrirReporte = async () => {
    try {
      await api.post(`/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/reabrir-reporte`);
      toast.success("Reabierto · vuelve a estar en la bandeja");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  // Cargar info de descargas del cliente (tracking ZIP)
  useEffect(() => {
    if (!reporte.peticion?.id) return undefined;
    let cancel = false;
    api.get(`/peticiones/${reporte.peticion.id}/portal-downloads`)
      .then((r) => { if (!cancel) setDownloadsInfo(r.data); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [reporte.peticion?.id]);

  const validar = async (aprobado) => {
    if (!aprobado && !notas.trim()) {
      toast.error("Pon el motivo de devolución");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/validar`, { aprobado, notas: notas || null });
      toast.success(aprobado ? "Reporte aprobado" : "Reporte devuelto al montador");
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const dir = [reporte.espacio?.direccion, reporte.espacio?.cp, reporte.espacio?.ciudad].filter(Boolean).join(", ");
  const maps = dir ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(dir)}` : null;
  const todasFotos = [p.foto_previa, ...(p.fotos_finales || [])].filter(Boolean);
  const aprobadasCount = todasFotos.filter((f) => f.aprobada_cliente).length;

  const toggleFotoAprobada = async (foto) => {
    try {
      await api.post(
        `/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/fotos/${foto.id}/aprobacion-cliente`,
        { aprobada: !foto.aprobada_cliente }
      );
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error aprobando foto");
    }
  };

  const aprobarTodas = async () => {
    try {
      await api.post(`/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/fotos/aprobar-todas`);
      toast.success("Todas las fotos marcadas para enviar al cliente");
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-amber-400">{reporte.codigo_ruta}</span>
            <span className="text-xs text-zinc-500">parada #{p.orden}</span>
            <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${lab.color}`}>{lab.label}</span>
            {p.tipo_incidencia && <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-zinc-800 text-zinc-300">{TIPO_INC[p.tipo_incidencia]}</span>}
            {(p.foto_previa_omitida || p.evidencia_incompleta) && (
              <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-500/40" data-testid="excepcion-evidencia-badge"
                title="Excepción de evidencia fotográfica">⚠ Evidencia</span>
            )}
            {puedeResetear && !p.reseteada && (
              <ResetParadaDialog rutaId={reporte.ruta_id} orden={p.orden} titulo={reporte.espacio?.nombre} className="ml-2" onDone={() => onUpdated?.()} />
            )}
          </div>
          <div className="text-base font-bold mt-0.5">{reporte.espacio?.nombre}</div>
          <div className="text-xs text-zinc-400">{reporte.cliente_nombre} · {reporte.peticion?.codigo}</div>
        </div>
        <div className="text-right text-xs text-zinc-500">
          <FechaMontajeEditable reporte={reporte} p={p} onChange={onChange} />
          <div>Por: {p.actualizado_por}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="bg-zinc-950/60 rounded p-2">
          <div className="text-zinc-500">Dirección</div>
          <div>{dir || "—"}</div>
          {maps && <a href={maps} target="_blank" rel="noreferrer" className="text-cyan-400 inline-flex items-center gap-0.5 mt-1"><ExternalLink className="w-3 h-3" /> Ver mapa</a>}
        </div>
        <div className="bg-zinc-950/60 rounded p-2">
          <div className="text-zinc-500">Cliente firma</div>
          <div>{p.cliente_firma_nombre || <span className="italic text-zinc-500">— sin firma —</span>}</div>
        </div>
      </div>

      {/* Iter 101 — Reporte manual: completar datos de facturación */}
      {(p.es_manual || (!p.linea_id && !reporte.peticion?.codigo)) && (
        <div
          className={`border rounded-lg p-3 text-xs ${p.manual_cliente_id && Number(p.manual_importe) > 0
            ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200"
            : "bg-amber-500/10 border-amber-500/40 text-amber-200"}`}
          data-testid="manual-datos-block">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="font-semibold mb-0.5">🧾 Reporte manual (sin petición)</div>
              {p.manual_cliente_id && Number(p.manual_importe) > 0 ? (
                <div data-testid="manual-datos-resumen">
                  Cliente: <b>{p.manual_cliente_nombre || "—"}</b> · Importe: <b>{Number(p.manual_importe).toFixed(2)} €</b>
                  {p.manual_concepto && <> · {p.manual_concepto}</>}
                </div>
              ) : (
                <div>Faltan datos para poder albaranar en FD: asigna <b>cliente</b> e <b>importe</b>.</div>
              )}
            </div>
            {!p.fd_delivery_note_id && (
              <Button size="sm" onClick={() => setShowCompletar(true)}
                data-testid="completar-manual-btn"
                className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold">
                ✏️ {p.manual_cliente_id ? "Editar datos" : "Completar datos"}
              </Button>
            )}
          </div>
        </div>
      )}
      {showCompletar && (
        <DialogCompletarManual reporte={reporte}
          onClose={() => setShowCompletar(false)}
          onDone={() => { setShowCompletar(false); onChange?.(); }} />
      )}

      {p.incidencia_descripcion && (
        <ObservacionesAdminBlock reporte={reporte} parada={p} onChange={onChange} />
      )}

      {/* Fase B · excepciones de evidencia fotográfica (trazadas, sin aprobación) */}
      {(p.foto_previa_omitida || p.evidencia_incompleta) && (
        <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-3 text-xs text-amber-100 space-y-1" data-testid="excepcion-evidencia-banner">
          <div className="font-semibold text-amber-200">⚠️ Excepción de evidencia fotográfica</div>
          {p.foto_previa_omitida && (
            <div data-testid="excepcion-foto-previa">
              <b>Sin foto previa</b> · {p.foto_previa_omitida.motivo_label || p.foto_previa_omitida.motivo}
              {p.foto_previa_omitida.detalle && <> — «{p.foto_previa_omitida.detalle}»</>}
              <span className="text-amber-200/60"> · {p.foto_previa_omitida.por} {(p.foto_previa_omitida.at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
          )}
          {p.evidencia_incompleta && (
            <div data-testid="excepcion-evidencia-incompleta">
              <b>Evidencia incompleta</b> ({p.evidencia_incompleta.n_fotos} foto{p.evidencia_incompleta.n_fotos === 1 ? "" : "s"} final{p.evidencia_incompleta.n_fotos === 1 ? "" : "es"}) · {p.evidencia_incompleta.motivo_label || p.evidencia_incompleta.motivo}
              {p.evidencia_incompleta.detalle && <> — «{p.evidencia_incompleta.detalle}»</>}
              <span className="text-amber-200/60"> · {p.evidencia_incompleta.por} {(p.evidencia_incompleta.at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
          )}
        </div>
      )}

      {p.revisita_creada_linea && (
        <div className="bg-cyan-500/10 border border-cyan-500/30 rounded p-2 text-xs text-cyan-200 flex items-center gap-2">
          🔁 Línea de 2ª visita generada (manual)
        </div>
      )}

      {/* DECISIÓN DE COBRO — sólo para incidencia/semi_montado/cancelado */}
      {["incidencia", "semi_montado", "cancelado"].includes(p.estado_cierre) && (
        <DecisionCobroPanel
          reporte={reporte}
          existente={p.decision_cobro}
          tipoIncidencia={p.tipo_incidencia}
          onSaved={() => onChange && onChange()}
        />
      )}

      {/* DETALLE DEL MONTAJE — misma fuente que RutaDetail / Mi Ruta (detalle_montaje) */}
      <DetalleMontajeReporte reporte={reporte} />

      {/* Ampliación de evidencias (oficina añade fotos / habilita al montador). No es resetear montaje. */}
      <FotosOficinaActions reporte={reporte} onChange={onChange} />

      {/* GRID FOTOS */}
      <div>
        <div className="text-xs font-semibold mb-2 flex items-center justify-between gap-1.5">
          <span className="flex items-center gap-1.5">
            <ImageIcon className="w-3.5 h-3.5 text-amber-400" /> Fotos ({todasFotos.length})
            {(() => { const c = contarOrigenFotos(todasFotos); return c.oficina > 0 ? <span className="text-zinc-500 font-normal" data-testid="fotos-origen-contador">· {c.montador} montador / {c.oficina} oficina</span> : null; })()}
            <span className="ml-2 text-emerald-300/90 font-normal" data-testid="fotos-aprobadas-count">
              · {aprobadasCount}/{todasFotos.length} aprobadas para cliente
            </span>
          </span>
          <span className="flex items-center gap-1.5">
          <DescargarFotosCarpeta reporte={reporte} fotos={todasFotos} />
          {todasFotos.length > 0 && aprobadasCount < todasFotos.length && (
            <button
              type="button"
              onClick={aprobarTodas}
              data-testid="aprobar-todas-fotos-btn"
              className="text-[10px] uppercase tracking-wider bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 border border-emerald-500/30 rounded px-2 py-0.5"
            >
              ✓ Aprobar todas
            </button>
          )}
          </span>
        </div>
        <div className="text-[10px] text-zinc-500 mb-2 italic">
          Marca con ✓ las fotos que se enviarán al cliente / agencia. Las no aprobadas quedan archivadas (sólo uso interno).
        </div>
        <div className="grid grid-cols-3 gap-2" data-testid="reporte-fotos">
          {todasFotos.map((f, idxFoto) => {
            const ok = !!f.aprobada_cliente;
            const isVideo = f.tipo === "video" || /\.(mp4|mov|webm|m4v)(\?|$)/i.test(f.url || "");
            return (
              <div
                key={f.id}
                className={`relative bg-zinc-950 rounded overflow-hidden border-2 transition-colors ${
                  ok ? "border-emerald-500/70" : "border-zinc-800"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setLightboxIdx(idxFoto)}
                  data-testid={`foto-thumb-${f.id}`}
                  className="block w-full text-left"
                >
                  {isVideo ? (
                    <div className="relative w-full h-28 bg-zinc-900">
                      <img
                        src={f.thumbnail_url || (f.url || "").replace(/\.(mp4|mov|webm|m4v)(\?.*)?$/i, ".jpg")}
                        alt="vídeo"
                        className={`w-full h-28 object-cover ${ok ? "" : "opacity-70"}`}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-black/60 rounded-full w-9 h-9 flex items-center justify-center">
                          <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white"><path d="M8 5v14l11-7z" /></svg>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <img src={cloudinaryThumb(f.url, 300, 200)} alt={f.categoria} className={`w-full h-28 object-cover ${ok ? "" : "opacity-70"}`} />
                  )}
                </button>
                <div className="absolute top-0.5 left-0.5 bg-black/75 rounded px-1 text-[9px] uppercase pointer-events-none">
                  {isVideo ? "🎥 vídeo" : f.categoria}
                </div>

                {/* Checkbox aprobación cliente */}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFotoAprobada(f); }}
                  data-testid={`toggle-foto-${f.id}`}
                  title={ok ? `Aprobada para cliente por ${f.aprobada_por || "—"}` : "Marcar para enviar al cliente"}
                  className={`absolute top-0.5 right-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold border transition-colors ${
                    ok
                      ? "bg-emerald-500 text-zinc-900 border-emerald-400"
                      : "bg-black/70 text-zinc-300 border-zinc-700 hover:border-emerald-500/50 hover:text-emerald-300"
                  }`}
                >
                  {ok ? "✓ Cliente" : "○ Pendiente"}
                </button>

                {f.latitud && (
                  <button type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(`https://maps.google.com/?q=${f.latitud},${f.longitud}`, "_blank"); }}
                    className="absolute bottom-0.5 left-0.5 bg-emerald-500/80 text-zinc-900 text-[9px] px-1 rounded flex items-center gap-0.5 hover:bg-emerald-400"
                    title="Abrir en Google Maps">
                    📍 GPS
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* FACTURACIÓN / IMPORTE (roles económicos; misma fuente que FD) */}
      {!p.es_manual && p.linea_id && esEconomico && (
        <>
          <ImporteTrabajo rutaId={reporte.ruta_id} idx={reporte.idx} refreshKey={`${p.fd_delivery_note_id || ""}|${p.albaran_local_id || ""}|${p.albaran_numero || ""}|${p.validacion?.estado || ""}|${factKey}`} onEco={setEco} />
          {fact?.aplica && !(p.albaran_numero || p.fd_delivery_note_id || p.fd_delivery_note_number) && (
            <RequisitosFacturacionBlock fact={fact} open={completarDatos} onOpenChange={setCompletarDatos}
              onSaved={() => { setFactKey((k) => k + 1); onUpdated?.(); }} />
          )}
        </>
      )}
      <ConfirmarCeroDialog open={!!cero} loading={fdLoading} onClose={() => setCero(null)}
        onConfirm={async (motivo) => { setFdLoading(true); try { const ok = await onEnviarFD(cero.nuevo, { confirmar_cero: true, motivo_cero: motivo }); if (ok) setCero(null); } finally { setFdLoading(false); } }} />
      {soft && (
        <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4" data-testid="soft-confirm-montaje-cero">
          <div className="bg-zinc-950 border border-amber-500/40 rounded-xl p-5 w-full max-w-md space-y-3 text-xs text-amber-200">
            <div className="font-bold text-amber-300 flex items-center gap-2 text-sm"><AlertTriangle className="w-5 h-5" /> Montaje a 0 €</div>
            <p>A FD irá <b>sólo la producción ({eco ? eur(eco.subtotal_produccion) : ""})</b>. ¿Continuar sin facturar el montaje? Si debe cobrarse, cancela y usa «Editar importe».</p>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setSoft(null)} data-testid="soft-confirm-cancelar">Cancelar</Button>
              <Button size="sm" onClick={async () => { const n = soft.nuevo; setSoft(null); await onEnviarFD(n); }} data-testid="soft-confirm-continuar" className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold">Continuar sin montaje</Button>
            </div>
          </div>
        </div>
      )}

      {/* ACCIONES */}
      {p.validacion?.estado === "pendiente" ? (
        <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-3 space-y-2">
          <div className="text-xs font-semibold">Validación de oficina</div>
          <Textarea rows={2} value={notas} onChange={(e) => setNotas(e.target.value)}
            placeholder="Notas (obligatorio si devuelves)"
            data-testid="validacion-notas"
            className="bg-zinc-900 border-zinc-800 text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => validar(false)} disabled={saving}
              data-testid="devolver-btn"
              className="bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-500/30">
              <XCircle className="w-4 h-4 mr-1.5" /> Devolver
            </Button>
            <Button onClick={() => validar(true)} disabled={saving}
              data-testid="aprobar-btn"
              className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Aprobar
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2 text-xs text-emerald-200 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> Aprobado por {p.validacion?.validador}
          {p.validacion?.notas && <span className="text-zinc-400">· {p.validacion.notas}</span>}
        </div>
      )}

      {p.validacion?.estado === "aprobado" && (
        <>
          {(() => {
            const hasAuto = (p.envios_cliente || []).length > 0;
            const hasManual = (p.envios || []).length > 0;
            const alreadySent = hasAuto || hasManual;
            return (
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={onEnviar}
                  className={alreadySent
                    ? "bg-zinc-700 hover:bg-zinc-600 text-zinc-100"
                    : "bg-cyan-500 hover:bg-cyan-400 text-zinc-900 font-bold"}
                  data-testid="enviar-cliente-btn"
                  title={hasAuto ? "Ya se envió automáticamente al validar. Esto reenvía a otro email." : ""}>
                  <Send className="w-4 h-4 mr-1.5" />
                  {alreadySent ? "Reenviar a otro email" : "Enviar al cliente"}
                </Button>
                {/* Estado FD */}
                {(() => {
                  const yaAlbaranado = !!(p.albaran_numero || p.fd_delivery_note_id || p.fd_delivery_note_number);
                  if (!yaAlbaranado && fdBloqueado) return <BotonFDBloqueado fact={fact} onCompletar={() => setCompletarDatos(true)} />;
                  return !yaAlbaranado ? (
                    <Button
                      onClick={() => pedirFD(false)}
                      className={`font-bold ${eco?.total_cero ? "bg-rose-500/80 hover:bg-rose-400 text-zinc-900" : "bg-emerald-500 hover:bg-emerald-400 text-zinc-900"}`}
                      data-testid="enviar-fd-btn"
                      title={eco?.total_cero ? "Importe 0 €: pedirá confirmación explícita" : "Crea albarán local + lo añade al albarán FD del mes para ese cliente (o crea uno nuevo si no existe)"}
                    >
                      <FileCheck2 className="w-4 h-4 mr-1.5" /> 📤 Enviar a FD{eco?.total_cero ? " (0 €)" : eco ? ` · ${eur(eco.total)}` : ""}
                    </Button>
                  ) : (
                    <Button onClick={onAlbaranar} variant="outline" className="bg-transparent border-zinc-700 text-zinc-400"
                      data-testid="albaranar-btn"
                      title="Editar número de albarán manual (si no usas FacturaDirecta)">
                      <FileCheck2 className="w-4 h-4 mr-1.5" /> Editar manual
                    </Button>
                  );
                })()}
              </div>
            );
          })()}
          {/* Botón discreto: marcar albarán manual cuando se crea fuera de la app
              (Jun 2026 — Fernando). Solo visible si AÚN NO está albaranado. */}
          {!(p.albaran_numero || p.fd_delivery_note_id || p.fd_delivery_note_number) && (
            <Button onClick={onAlbaranar} variant="outline" size="sm"
              className="w-full bg-transparent border-zinc-700 text-zinc-400 hover:text-zinc-200 hover:border-zinc-500"
              data-testid="albaranar-manual-btn"
              title="Si el albarán lo hiciste a mano en FD o fuera de la app, márcalo aquí para que la lista te muestre la etiqueta verde.">
              <FileCheck2 className="w-3.5 h-3.5 mr-1.5" /> Marcar albarán creado manualmente
            </Button>
          )}
          {p.estado_cierre !== "montado" && (
            <Button onClick={async () => {
              try {
                const r = await api.get(`/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/incidencia.pdf`, { responseType: "blob" });
                const url = URL.createObjectURL(r.data);
                window.open(url, "_blank");
              } catch (err) { toast.error("Error generando PDF"); }
            }} variant="outline" className="w-full bg-transparent border-amber-600/40 text-amber-300 hover:bg-amber-500/10"
              data-testid="pdf-incidencia-btn">
              <Download className="w-4 h-4 mr-1.5" /> Descargar PDF de incidencia
            </Button>
          )}
          {p.envios && p.envios.length > 0 && (
            <div className="bg-violet-500/10 border border-violet-500/30 rounded p-2 text-xs text-violet-200">
              📤 Enviado manual {p.envios.length} vez(es). Último: {p.envios[p.envios.length - 1].canal} · {new Date(p.envios[p.envios.length - 1].at).toLocaleString("es-ES")}
            </div>
          )}
          {p.envios_cliente && p.envios_cliente.length > 0 && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2 text-xs text-emerald-200">
              <div className="font-semibold mb-1">✨ Envíos automáticos al cliente (tras validar):</div>
              <ul className="space-y-0.5">
                {p.envios_cliente.slice(-3).map((e, i) => {
                  const canal = (e.canal || "").startsWith("whatsapp") ? "📱 WhatsApp" :
                                (e.canal || "").startsWith("email") ? "📧 Email" : e.canal;
                  return (
                    <li key={i} className="text-[11px]">
                      {canal} → <span className="font-mono">{e.destinatario}</span> · {new Date(e.enviado_at || e.at).toLocaleString("es-ES")}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {/* 🏁 Botón "The End" — Fernando jun 2026: cierra explícitamente el reporte
              y lo saca de la bandeja, incluso si los flags binarios no cumplen todos.
              Visible solo si validación aprobada. */}
          {p.validacion?.estado === "aprobado" && !p.reporte_finalizado_at && (
            <Button onClick={onFinalizarReporte}
              className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-zinc-950 hover:from-emerald-400 hover:to-emerald-500 font-bold"
              data-testid="finalizar-reporte-btn"
              title="Cierra este reporte definitivamente: lo saca de la bandeja y queda archivado. Si te equivocas, puedes reabrirlo desde 'Ver finalizados'.">
              🏁 Finalizar reporte (The End)
            </Button>
          )}
          {p.reporte_finalizado_at && (
            <div className="bg-emerald-600/15 border border-emerald-600/40 rounded p-2 text-xs text-emerald-200 flex items-center justify-between gap-2">
              <span>🏁 Reporte finalizado el {new Date(p.reporte_finalizado_at).toLocaleString("es-ES")}
                {p.reporte_finalizado_por && <> por <b>{p.reporte_finalizado_por}</b></>}
              </span>
              <Button onClick={onReabrirReporte} variant="ghost" size="sm"
                className="text-emerald-300 hover:text-emerald-100 text-[10px] h-6 px-2"
                data-testid="reabrir-reporte-btn">
                Reabrir
              </Button>
            </div>
          )}
          {/* Tracking de descarga ZIP por el cliente (auditoría) */}
          {downloadsInfo && downloadsInfo.downloads_count > 0 ? (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2 text-xs text-emerald-200" data-testid="cliente-descargo-zip">
              ⬇️ Cliente descargó las fotos {downloadsInfo.downloads_count} vez(es).
              Última: {new Date(downloadsInfo.last_download_at).toLocaleString("es-ES")}
            </div>
          ) : (downloadsInfo && p.envios && p.envios.length > 0) ? (
            <div className="bg-zinc-800/40 border border-zinc-700 rounded p-2 text-xs text-zinc-400" data-testid="cliente-no-descargo">
              ⏳ Aún no ha descargado las fotos
              {downloadsInfo.portal_views_count > 0 && (
                <span> · pero entró al portal {downloadsInfo.portal_views_count} vez(es)</span>
              )}
            </div>
          ) : null}
          {(p.albaran_numero || p.fd_delivery_note_id || p.fd_delivery_note_number) && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2 text-xs text-emerald-200 flex items-center justify-between gap-2"
                 data-testid="albaran-info-banner">
              <span>
                🧾 {p.albaran_numero
                  ? <>Albarán <b>{p.albaran_numero}</b></>
                  : <>Enviado a FacturaDirecta</>}
                {p.fd_delivery_note_number && (
                  <>
                    {" "}· FD <b className="font-mono text-emerald-300">{p.fd_delivery_note_number}</b>
                  </>
                )}
              </span>
              <div className="flex items-center gap-2">
                {p.fd_delivery_note_id && (
                  <a
                    href={`https://app.facturadirecta.com/#/deliveryNotes/${p.fd_delivery_note_id}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="ver-fd-link"
                    className="text-[10px] text-cyan-300 hover:text-cyan-200 underline-offset-2 hover:underline flex items-center gap-0.5"
                    title="Abrir el albarán en FacturaDirecta"
                  >
                    Ver en FD <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {!p.fd_delivery_note_number && p.albaran_fd_error && (
                  <span className="text-rose-300 text-[10px]" title={p.albaran_fd_error}>
                    ⚠️ FD falló
                  </span>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <PhotoLightbox
        fotos={todasFotos}
        openIdx={lightboxIdx}
        onClose={() => setLightboxIdx(null)}
        title={`${reporte.espacio?.nombre || ""} · ${reporte.peticion?.codigo || ""}`}
      />
    </div>
  );
}

function DialogEnvio({ reporte, onClose, onDone }) {
  const p = reporte.parada;
  const [canal, setCanal] = useState("whatsapp");
  // Precarga: tel/email del cliente principal o del primer contacto que lo tenga
  const _telDefault = (() => {
    if (reporte.cliente_telefono) return String(reporte.cliente_telefono);
    const c = (reporte.cliente_contactos || []).find((x) => x.telefono);
    return c ? String(c.telefono) : "";
  })();
  const _emailDefault = (() => {
    if (reporte.cliente_email) return String(reporte.cliente_email);
    const c = (reporte.cliente_contactos || []).find((x) => x.email);
    return c ? String(c.email) : "";
  })();
  const [tel, setTel] = useState(_telDefault);
  const [email, setEmail] = useState(_emailDefault);
  const [notas, setNotas] = useState("");
  const [waUrl, setWaUrl] = useState(null); // fallback si popup bloqueado
  const [linkInfo, setLinkInfo] = useState(null); // { portal_path, mensaje_whatsapp, ... }
  // SOLO fotos aprobadas para cliente
  const fotos = [p.foto_previa, ...(p.fotos_finales || [])].filter((f) => f && f.aprobada_cliente);
  const totalFotos = [p.foto_previa, ...(p.fotos_finales || [])].filter(Boolean).length;
  const sinAprobadas = fotos.length === 0;

  // Cargar enlace al portal del cliente para este reporte (solo una vez al abrir el diálogo)
  useEffect(() => {
    let cancel = false;
    api.get(`/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/portal-link`)
      .then((r) => { if (!cancel) setLinkInfo(r.data); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [reporte.ruta_id, reporte.idx]);

  // Construir URL absoluta del portal con el origin actual
  const portalUrlAbs = linkInfo?.portal_path
    ? `${window.location.origin}${linkInfo.portal_path}`
    : null;

  // Mensaje: si tenemos linkInfo, usamos su template (que ya incluye el enlace)
  // si no, fallback a versión sin enlace (mientras carga / si falla)
  const cierreLabel = (CIERRE_LABEL[p.estado_cierre] || {}).label || "—";
  const msgFallback = `Hola,\n\nTe paso el reporte de montaje *${reporte.peticion?.codigo || ""}* de *RotulaciónDNF*.\n\nTienda: *${reporte.espacio?.nombre || ""}*${reporte.espacio?.direccion ? `\nDirección: ${reporte.espacio.direccion}` : ""}\nEstado: *${cierreLabel}*${p.tipo_incidencia ? ` (${TIPO_INC[p.tipo_incidencia]})` : ""}${p.incidencia_descripcion ? `\n\nObservaciones: ${p.incidencia_descripcion}` : ""}\n\nUn saludo,\nRotulaciónDNF · +34 624 49 12 71 · rotulaciondnf.com`;
  const msgCorto = (linkInfo?.mensaje_whatsapp && portalUrlAbs)
    ? linkInfo.mensaje_whatsapp.replace("{PORTAL_URL}", portalUrlAbs)
    : msgFallback;

  const enviarWA = async () => {
    if (!tel) return toast.error("Indica el teléfono");
    // Limpieza: deja solo dígitos
    let numero = String(tel).replace(/\D/g, "");
    // Si empieza por 00 lo quitamos (formato internacional alternativo)
    if (numero.startsWith("00")) numero = numero.slice(2);
    // Si no lleva prefijo internacional y son 9 dígitos → asumimos España (+34)
    if (numero.length === 9) numero = "34" + numero;
    if (numero.length < 10) return toast.error("Teléfono no válido (incluye prefijo país, ej. 34...)");
    const url = `https://wa.me/${numero}?text=${encodeURIComponent(msgCorto)}`;
    // Intentar abrir; si el navegador bloquea, mostrar enlace clickeable + copiar
    let win = null;
    try { win = window.open(url, "_blank", "noopener,noreferrer"); } catch (e) { win = null; }
    if (!win || win.closed || typeof win.closed === "undefined") {
      // popup bloqueado: mostrar enlace + copiar texto
      setWaUrl(url);
      try { await navigator.clipboard.writeText(msgCorto); } catch (e) { /* ignore */ }
      toast.warning("El navegador bloqueó la ventana. Pulsa el botón verde de abajo para abrir WhatsApp.");
    } else {
      setWaUrl(null);
      toast.success(portalUrlAbs
        ? "Mensaje preparado. El cliente verá las fotos en el portal."
        : `Mensaje preparado. Adjunta las ${fotos.length} fotos desde tu galería.`);
    }
    await registrar("whatsapp", tel);
  };

  const enviarEmail = async () => {
    if (!email) return toast.error("Indica el email");
    try {
      toast.info("Enviando email con logos...");
      const { data } = await api.post(`/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/enviar-email-cliente`, { email });
      toast.success(`Email enviado a ${email}`);
      await registrar("email", email);
      return data;
    } catch (err) {
      // Fallback: abrir mailto local si el endpoint falla
      const subject = `Reporte montaje ${reporte.peticion?.codigo || ""} - ${reporte.espacio?.nombre || ""}`;
      let body = msgCorto;
      if (!portalUrlAbs) {
        const linksFotos = fotos.map((f, i) => `${i + 1}. ${f.url}`).join("\n");
        body += `\n\n--- Enlaces a las fotos ---\n${linksFotos}`;
      }
      toast.warning("Email server no disponible, abriendo cliente local...");
      window.open(`mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
      await registrar("email", email);
    }
  };

  const descargarFotos = async () => {
    // Iter 96 — Forzar descarga real (no abrir pestaña).
    // Cloudinary URLs sin `fl_attachment` se abren en nueva pestaña por defecto.
    // Truco: insertamos `/fl_attachment/` en la URL de Cloudinary para que el
    // navegador descargue como adjunto. Para URLs no-Cloudinary, hacemos fetch
    // + Blob + URL.createObjectURL como fallback.
    let ok = 0, fail = 0;
    for (const f of fotos) {
      try {
        let url = f.url;
        const filename = `${reporte.peticion?.codigo || "foto"}_${f.categoria || "img"}_${ok + fail + 1}.jpg`;
        // Cloudinary: insertar fl_attachment para forzar Content-Disposition
        if (url.includes("res.cloudinary.com") && !url.includes("fl_attachment")) {
          url = url.replace("/upload/", "/upload/fl_attachment/");
        }
        // Fetch como blob (evita "abrir en pestaña" para URLs sin attachment header)
        const resp = await fetch(url, { mode: "cors", credentials: "omit" });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        // Liberar el blob URL tras 1s
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        ok++;
        await new Promise(r => setTimeout(r, 200));
      } catch (e) {
        fail++;
        console.error("Fallo descarga", f.url, e);
      }
    }
    if (fail === 0) {
      toast.success(`✅ ${ok} foto(s) descargada(s)`);
    } else {
      toast.warning(`Descargadas ${ok}/${fotos.length}. ${fail} fallaron (revisa permisos CORS).`);
    }
  };

  const registrar = async (c, dest) => {
    try {
      await api.post(`/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/enviado-cliente`,
        { canal: c, destinatario: dest, notas });
      onDone && onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const marcarManual = async () => {
    await registrar(canal, canal === "app" ? "Subido a app cliente" : "Enviado manualmente");
    toast.success("Envío registrado");
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 w-[95vw] sm:w-auto max-w-[640px] max-h-[88vh] overflow-y-auto overflow-x-hidden p-4">
        <DialogHeader><DialogTitle>📤 Enviar reporte al cliente</DialogTitle></DialogHeader>
        <div className="space-y-3 min-w-0">
          {sinAprobadas ? (
            <div className="bg-red-500/15 border border-red-500/40 rounded p-3 text-xs text-red-200">
              ⚠️ <b>No hay fotos aprobadas todavía.</b> Vuelve al detalle del reporte y marca con ✓ las fotos que quieres enviar al cliente.
              <div className="text-zinc-400 mt-1">Total subidas por el montador: {totalFotos}</div>
            </div>
          ) : (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2 text-[11px] text-emerald-200">
              Se enviarán <b>{fotos.length}</b> de {totalFotos} foto(s) marcadas como aprobadas para cliente.
            </div>
          )}
          {/* Iter 96 — Channel selector: grid 2x2 que se adapta. */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { k: "whatsapp", label: "WhatsApp", icon: MessageCircle },
              { k: "wa_fotos", label: "WA + Fotos", icon: ImageIcon },
              { k: "email", label: "Email", icon: Mail },
              { k: "app", label: "App cliente", icon: Smartphone },
              { k: "manual", label: "Manual", icon: Send },
            ].map((opt) => {
              const I = opt.icon;
              return (
                <button key={opt.k} onClick={() => setCanal(opt.k)}
                  data-testid={`canal-${opt.k}`}
                  className={`flex items-center justify-center gap-1.5 py-2 px-2 rounded border text-xs min-w-0 ${
                    canal === opt.k ? "bg-cyan-500/20 text-cyan-100 border-cyan-500/40" : "bg-zinc-900 border-zinc-800 text-zinc-400"
                  }`}><I className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{opt.label}</span></button>
              );
            })}
          </div>

          {canal === "whatsapp" && (
            <div className="space-y-2">
              <Input value={tel} onChange={(e) => setTel(e.target.value)} placeholder="34600123456 (con prefijo, sin +)"
                data-testid="wa-telefono" className="bg-zinc-900 border-zinc-800" />
              <div className="bg-zinc-900/60 rounded p-2 text-[10px] text-zinc-400 whitespace-pre-wrap max-h-32 overflow-y-auto">{msgCorto}</div>
              {portalUrlAbs ? (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2 text-[10px] text-emerald-200">
                  ✅ El mensaje incluye un <b>enlace al portal del cliente</b> donde verá las fotos y podrá descargar el ZIP. No hace falta adjuntar nada.
                </div>
              ) : (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 text-[10px] text-amber-200">
                  ⚠️ No se ha podido generar el enlace al portal. Adjunta las fotos manualmente desde galería.
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={descargarFotos} variant="outline"
                  data-testid="descargar-fotos-btn"
                  className="bg-transparent border-zinc-700 text-zinc-200">
                  ⬇️ Descargar fotos ({fotos.length})
                </Button>
                <Button onClick={enviarWA} className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold"
                  data-testid="abrir-whatsapp-btn">
                  📱 Abrir WhatsApp
                </Button>
              </div>
              {waUrl && (
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="wa-fallback-link"
                  className="block w-full text-center bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold rounded py-2 text-sm"
                  onClick={() => setWaUrl(null)}
                >
                  ➡️ Pulsa aquí para abrir WhatsApp (texto copiado al portapapeles)
                </a>
              )}
            </div>
          )}

          {canal === "wa_fotos" && (
            <WhatsAppFotosSection reporte={reporte} fotos={fotos} telDefault={_telDefault} onDone={onDone} />
          )}

          {canal === "email" && (
            <div className="space-y-2">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cliente@empresa.com"
                data-testid="email-destinatario" className="bg-zinc-900 border-zinc-800" />
              <div className="text-[10px] text-zinc-500">El email incluirá los enlaces a las {fotos.length} fotos para que el cliente las descargue.</div>
              <Button onClick={enviarEmail} className="w-full bg-cyan-500 hover:bg-cyan-400 text-zinc-900 font-bold">
                Abrir email con mensaje + enlaces
              </Button>
            </div>
          )}

          {(canal === "app" || canal === "manual") && (
            <div className="space-y-2">
              <Textarea rows={2} value={notas} onChange={(e) => setNotas(e.target.value)}
                placeholder={canal === "app" ? "Referencia subida a la app del cliente..." : "Cómo se envió..."}
                className="bg-zinc-900 border-zinc-800 text-sm" />
              <Button onClick={marcarManual} className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold">
                Marcar como enviado
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Jun-2026: envío por WhatsApp Web con las fotos adjuntas (JPG renombrados con
// el espacio) + PDF de incidencia si aplica. Móvil: share nativo (adjunta solo).
// Escritorio: descarga los archivos renombrados + abre el chat con el mensaje.
function sanitizeNombreArchivo(s) {
  const limpio = (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9 _-]/g, "").trim().replace(/\s+/g, "_").slice(0, 60);
  return limpio || "reporte";
}

function WhatsAppFotosSection({ reporte, fotos, telDefault, onDone }) {
  const p = reporte.parada;
  const esIncidencia = p.estado_cierre === "incidencia";
  const espacioNombre = reporte.espacio?.nombre || reporte.peticion?.codigo || "montaje";
  const fechaEfectiva = String(p.fecha_montaje_real || p.finalizado_at || reporte.fecha || "").slice(0, 10);
  const fechaTxt = fechaEfectiva ? new Date(fechaEfectiva + "T00:00:00").toLocaleDateString("es-ES") : "—";
  const [tel1, setTel1] = useState(telDefault || "");
  const [tel2, setTel2] = useState("");
  const [msg, setMsg] = useState(
    esIncidencia
      ? `Reporte con INCIDENCIA realizado en "${espacioNombre}" el día ${fechaTxt}. Adjuntamos las fotos y el PDF de la incidencia.\n\nRotulaciónDNF · +34 624 49 12 71`
      : `Reporte realizado en "${espacioNombre}" el día ${fechaTxt}. Adjuntamos las fotos.\n\nRotulaciónDNF · +34 624 49 12 71`
  );
  const [files, setFiles] = useState(null); // null = preparando
  const [prepError, setPrepError] = useState(null);
  const [enviados, setEnviados] = useState([]); // teléfonos ya enviados

  // Pre-preparar los archivos al abrir la sección para que el share sea instantáneo
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const slug = sanitizeNombreArchivo(espacioNombre);
        const out = [];
        let i = 0;
        for (const f of fotos) {
          i++;
          let url = f.url;
          if (url.includes("res.cloudinary.com") && url.includes("/upload/") && !url.includes("f_jpg")) {
            url = url.replace("/upload/", "/upload/f_jpg/");
          }
          const resp = await fetch(url, { mode: "cors", credentials: "omit" });
          if (!resp.ok) throw new Error(`Foto ${i}: HTTP ${resp.status}`);
          const blob = await resp.blob();
          out.push(new File([blob], `${slug}_${i}.jpg`, { type: "image/jpeg" }));
        }
        if (esIncidencia) {
          const r = await api.get(`/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/incidencia.pdf`, { responseType: "blob" });
          out.push(new File([r.data], `Incidencia_${slug}.pdf`, { type: "application/pdf" }));
        }
        if (!cancel) setFiles(out);
      } catch (e) {
        if (!cancel) { setPrepError(String(e?.message || e)); setFiles([]); }
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line
  }, []);

  const normalizar = (t) => {
    let n = String(t || "").replace(/\D/g, "");
    if (n.startsWith("00")) n = n.slice(2);
    if (n.length === 9) n = "34" + n;
    return n.length >= 10 ? n : null;
  };

  const registrarEnvio = async (dest, cerrar) => {
    try {
      await api.post(`/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/enviado-cliente`,
        { canal: "whatsapp", destinatario: dest, notas: `WA con ${(files || []).length} adjunto(s)` });
      if (cerrar) onDone && onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Error registrando el envío"); }
  };

  const puedeShare = typeof navigator !== "undefined" && !!navigator.canShare
    && files && files.length > 0 && navigator.canShare({ files });

  const enviarA = async (telRaw, esUltimo) => {
    const num = normalizar(telRaw);
    if (!num) return toast.error("Teléfono no válido (incluye prefijo país, ej. 34...)");
    try { await navigator.clipboard.writeText(msg); } catch (e) { /* ignore */ }
    if (puedeShare) {
      try {
        await navigator.share({ files, text: msg });
        toast.success("Elige el chat de WhatsApp del cliente. El texto está copiado por si no se pega solo.");
        setEnviados((v) => [...v, telRaw]);
        await registrarEnvio(telRaw, esUltimo);
        return;
      } catch (e) {
        if (e && e.name === "AbortError") return; // usuario canceló el share
      }
    }
    // Escritorio: descargar renombrados + abrir chat con el mensaje
    for (const f of files || []) {
      const blobUrl = URL.createObjectURL(f);
      const a = document.createElement("a");
      a.href = blobUrl; a.download = f.name;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
      await new Promise((r) => setTimeout(r, 250));
    }
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
    toast.success(`${(files || []).length} archivo(s) descargados. Arrástralos al chat que se ha abierto.`, { duration: 8000 });
    setEnviados((v) => [...v, telRaw]);
    await registrarEnvio(telRaw, esUltimo);
  };

  const preparando = files === null;
  const nFotos = fotos.length;

  return (
    <div className="space-y-2" data-testid="wa-fotos-section">
      <div className={`rounded p-2 text-[11px] border ${
        preparando ? "bg-zinc-900/60 border-zinc-800 text-zinc-400"
        : prepError ? "bg-red-500/10 border-red-500/30 text-red-200"
        : files.length === 0 ? "bg-amber-500/10 border-amber-500/30 text-amber-200"
        : "bg-emerald-500/10 border-emerald-500/30 text-emerald-200"}`}
        data-testid="wa-fotos-estado">
        {preparando ? (
          <span className="flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Preparando {nFotos} foto(s){esIncidencia ? " + PDF de incidencia" : ""}...</span>
        ) : prepError ? (
          <>⚠️ Error preparando adjuntos: {prepError}</>
        ) : files.length === 0 ? (
          <>⚠️ No hay adjuntos que enviar. Aprueba primero las fotos con ✓ en el detalle del reporte.</>
        ) : (
          <>✅ {files.length} adjunto(s) listos: {nFotos} foto(s) JPG renombradas con el espacio{esIncidencia ? " + PDF de incidencia" : ""}.</>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Input value={tel1} onChange={(e) => setTel1(e.target.value)} placeholder="Teléfono 1 · 34600123456"
          data-testid="wa-fotos-tel1" className="bg-zinc-900 border-zinc-800" />
        <Input value={tel2} onChange={(e) => setTel2(e.target.value)} placeholder="Teléfono 2 (opcional)"
          data-testid="wa-fotos-tel2" className="bg-zinc-900 border-zinc-800" />
      </div>
      <Textarea rows={4} value={msg} onChange={(e) => setMsg(e.target.value)}
        data-testid="wa-fotos-mensaje" className="bg-zinc-900 border-zinc-800 text-xs" />
      <div className="bg-zinc-900/60 rounded p-2 text-[10px] text-zinc-400">
        {puedeShare
          ? "📱 Se abrirá el menú de compartir: elige WhatsApp y el chat del cliente. Las fotos van adjuntas automáticamente."
          : "💻 Se descargarán los archivos renombrados y se abrirá el chat de WhatsApp con el mensaje escrito. Solo tienes que arrastrar los archivos al chat."}
      </div>
      <div className={`grid gap-2 ${tel2.trim() ? "grid-cols-2" : "grid-cols-1"}`}>
        <Button onClick={() => enviarA(tel1, !tel2.trim())} disabled={preparando || !!prepError || (files && files.length === 0)}
          className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold"
          data-testid="wa-fotos-enviar-1">
          {enviados.includes(tel1) ? "✓ Enviado · Reenviar nº1" : "📤 Enviar al nº 1"}
        </Button>
        {tel2.trim() && (
          <Button onClick={() => enviarA(tel2, true)} disabled={preparando || !!prepError || (files && files.length === 0)}
            className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold"
            data-testid="wa-fotos-enviar-2">
            {enviados.includes(tel2) ? "✓ Enviado · Reenviar nº2" : "📤 Enviar al nº 2"}
          </Button>
        )}
      </div>
    </div>
  );
}


function DialogAlbaran({ reporte, onClose, onDone }) {
  const [numero, setNumero] = useState("");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const albaranar = async () => {
    if (!numero.trim()) return toast.error("Indica el número de albarán");
    try {
      await api.post(`/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/albaranar`, { numero, fecha });
      toast.success(`Albarán ${numero} registrado`);
      onDone && onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-sm">
        <DialogHeader><DialogTitle>🧾 Registrar albarán</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-zinc-400">
            Genera el albarán en Factusol/FacturaDirecta y pega aquí el número para registrarlo en la app.
          </div>
          <div>
            <label className="text-xs text-zinc-500">Nº albarán</label>
            <Input value={numero} onChange={(e) => setNumero(e.target.value)}
              placeholder="A-2026-0001" data-testid="albaran-numero"
              className="bg-zinc-900 border-zinc-800" />
          </div>
          <div>
            <label className="text-xs text-zinc-500">Fecha</label>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)}
              className="bg-zinc-900 border-zinc-800" />
          </div>
          <Button onClick={albaranar} className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold"
            data-testid="confirmar-albaran-btn">
            Guardar albarán
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const CULPA_LABEL = {
  cliente: "Culpa Cliente",
  produccion_culpa_cliente: "Producción (culpa cliente)",
  dnf: "Culpa DNF",
};
const CULPA_HINT = {
  cliente: "P. ej. cliente cambia diseño en obra · cliente da medidas mal · espacio bloqueado",
  produccion_culpa_cliente: "Hay que rehacer material por error de medida del cliente — cobramos material",
  dnf: "Error nuestro (medida, producción, transporte) — no cobramos 1ª visita ni material",
};

function DecisionCobroPanel({ reporte, existente, tipoIncidencia, onSaved }) {
  const [open, setOpen] = useState(!existente);
  const [culpa, setCulpa] = useState(existente?.culpa || "cliente");
  const [montaje, setMontaje] = useState(existente?.montaje_1a_pct ?? 65);
  const [material, setMaterial] = useState(existente?.material_1a_pct ?? 0);
  const [desp, setDesp] = useState(existente?.desplazamiento_1a ?? true);
  const [cobrar2a, setCobrar2a] = useState(existente?.cobrar_2a_visita ?? true);
  const [generar2a, setGenerar2a] = useState(existente?.generar_2a_visita ?? false);
  // Bug fix Jun 2026: `tipoIncidencia` puede valer "otros", "material_dañado", etc.
  // — son tipos de INCIDENCIA, no de REVISITA. Solo aceptamos "tiempo"/"medida" como pre-valor.
  const tipoInicial = (existente?.tipo_revisita
    || (tipoIncidencia === "tiempo" || tipoIncidencia === "medida" ? tipoIncidencia : null)
    || "tiempo");
  const [tipo, setTipo] = useState(tipoInicial);
  const [fechaPropuesta, setFechaPropuesta] = useState(existente?.fecha_propuesta_revisita || "");
  const [notas, setNotas] = useState(existente?.notas || "");
  const [saving, setSaving] = useState(false);

  // Al cambiar culpa → aplicar sugerencia
  const cambiarCulpa = async (newCulpa) => {
    setCulpa(newCulpa);
    try {
      const { data } = await api.get(
        `/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/sugerencia-cobro?culpa=${newCulpa}`
      );
      setMontaje(data.montaje_1a_pct);
      setMaterial(data.material_1a_pct);
      setDesp(data.desplazamiento_1a);
      setCobrar2a(data.cobrar_2a_visita);
    } catch (err) { /* silent */ }
  };

  const guardar = async () => {
    setSaving(true);
    try {
      await api.post(
        `/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/decidir-cobro-revisita`,
        {
          culpa,
          montaje_1a_pct: Number(montaje),
          material_1a_pct: Number(material),
          desplazamiento_1a: desp,
          cobrar_2a_visita: cobrar2a,
          generar_2a_visita: generar2a,
          tipo_revisita: generar2a ? tipo : null,
          fecha_propuesta_revisita: generar2a && fechaPropuesta ? fechaPropuesta : null,
          notas: notas || null,
        }
      );
      toast.success("Decisión guardada");
      onSaved && onSaved();
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardando");
    } finally { setSaving(false); }
  };

  if (existente && !open) {
    return (
      <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-3 space-y-1 text-xs" data-testid="decision-cobro-resumen">
        <div className="flex items-center justify-between gap-2">
          <div className="font-semibold text-violet-200 flex items-center gap-1.5">
            💰 Decisión de cobro · {CULPA_LABEL[existente.culpa]}
          </div>
          <button onClick={() => setOpen(true)} className="text-[10px] text-cyan-400 underline" data-testid="decision-cobro-editar">
            Editar
          </button>
        </div>
        <div className="text-zinc-300 text-[11px]">
          Montaje 1ª: <b>{existente.montaje_1a_pct}%</b> · Material: <b>{existente.material_1a_pct}%</b> ·
          Despl: <b>{existente.desplazamiento_1a ? "Sí" : "No"}</b> · 2ª visita: <b>{existente.cobrar_2a_visita ? "Cobrada" : "No cobrada"}</b>
          {existente.generar_2a_visita && <> · 🔁 <b>2ª visita generada</b></>}
        </div>
        {existente.notas && <div className="text-zinc-400 text-[10px] italic">«{existente.notas}»</div>}
        <div className="text-[10px] text-zinc-500">Por {existente.decidida_por} · {existente.decidida_at ? new Date(existente.decidida_at).toLocaleString("es-ES") : ""}</div>
      </div>
    );
  }

  return (
    <div className="bg-violet-500/10 border-2 border-violet-500/40 rounded-lg p-3 space-y-3" data-testid="decision-cobro-form">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-violet-200 text-sm">💰 Decisión de cobro & 2ª visita</div>
        {existente && (
          <button onClick={() => setOpen(false)} className="text-[10px] text-zinc-400 underline">Cancelar</button>
        )}
      </div>

      {/* Selector culpa */}
      <div>
        <div className="text-[10px] uppercase text-zinc-400 mb-1">¿De quién es la culpa?</div>
        <div className="grid grid-cols-3 gap-1.5">
          {["cliente", "produccion_culpa_cliente", "dnf"].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => cambiarCulpa(c)}
              data-testid={`culpa-${c}`}
              className={`px-2 py-2 rounded text-[11px] font-semibold border transition-colors ${
                culpa === c
                  ? "bg-violet-500 text-zinc-900 border-violet-400"
                  : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-600"
              }`}
            >
              {CULPA_LABEL[c]}
            </button>
          ))}
        </div>
        <div className="text-[10px] text-zinc-500 mt-1.5 italic">{CULPA_HINT[culpa]}</div>
      </div>

      {/* Cobro 1ª visita */}
      <div className="bg-zinc-950/50 rounded p-2.5 space-y-2">
        <div className="text-[10px] uppercase text-zinc-400">Cobro 1ª visita</div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <label className="flex items-center justify-between gap-2">
            <span>Montaje 1ª</span>
            <select value={montaje} onChange={(e) => setMontaje(e.target.value)} data-testid="montaje-1a-pct"
              className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs">
              <option value={0}>0%</option>
              <option value={65}>65%</option>
              <option value={100}>100%</option>
            </select>
          </label>
          <label className="flex items-center justify-between gap-2">
            <span>Material 1ª</span>
            <select value={material} onChange={(e) => setMaterial(e.target.value)} data-testid="material-1a-pct"
              className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs">
              <option value={0}>0%</option>
              <option value={100}>100%</option>
            </select>
          </label>
          <label className="flex items-center justify-between gap-2 col-span-2">
            <span>Desplazamiento 1ª</span>
            <input type="checkbox" checked={desp} onChange={(e) => setDesp(e.target.checked)} data-testid="desp-1a"
              className="w-4 h-4 accent-violet-500" />
          </label>
        </div>
      </div>

      {/* 2ª visita */}
      <div className="bg-zinc-950/50 rounded p-2.5 space-y-2">
        <div className="text-[10px] uppercase text-zinc-400">2ª visita</div>
        <label className="flex items-center justify-between gap-2 text-xs">
          <span>Generar línea de 2ª visita</span>
          <input type="checkbox" checked={generar2a} onChange={(e) => setGenerar2a(e.target.checked)} data-testid="generar-2a"
            className="w-4 h-4 accent-violet-500" />
        </label>
        {generar2a && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <label className="flex items-center justify-between gap-2">
                <span>Tipo</span>
                <select value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="tipo-revisita"
                  className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs">
                  <option value="tiempo">Tiempo (material OK)</option>
                  <option value="medida">Medida (rehacer material)</option>
                </select>
              </label>
              <label className="flex items-center justify-between gap-2">
                <span>Cobrar 2ª visita</span>
                <input type="checkbox" checked={cobrar2a} onChange={(e) => setCobrar2a(e.target.checked)} data-testid="cobrar-2a"
                  className="w-4 h-4 accent-violet-500" />
              </label>
            </div>
            <label className="flex items-center justify-between gap-2 text-xs">
              <span className="text-zinc-300">📅 Fecha propuesta 2ª visita <span className="text-zinc-500">(opcional)</span></span>
              <input type="date" value={fechaPropuesta} onChange={(e) => setFechaPropuesta(e.target.value)}
                data-testid="fecha-propuesta-revisita"
                className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-100" />
            </label>
            {fechaPropuesta && (
              <div className="text-[10px] text-violet-300 bg-violet-500/10 border border-violet-500/30 rounded px-2 py-1">
                💡 La línea de 2ª visita aparecerá marcada con esta fecha en el planner de rutas.
              </div>
            )}
          </div>
        )}
        {!generar2a && (
          <label className="flex items-center justify-between gap-2 text-xs">
            <span>Cobrar trabajo extra al cliente</span>
            <input type="checkbox" checked={cobrar2a} onChange={(e) => setCobrar2a(e.target.checked)}
              className="w-4 h-4 accent-violet-500" />
          </label>
        )}
      </div>

      <Textarea rows={2} value={notas} onChange={(e) => setNotas(e.target.value)}
        placeholder="Notas internas sobre la decisión (opcional)"
        data-testid="decision-notas"
        className="bg-zinc-900 border-zinc-800 text-xs" />

      <Button onClick={guardar} disabled={saving}
        data-testid="guardar-decision-cobro"
        className="w-full bg-violet-500 hover:bg-violet-400 text-zinc-900 font-bold">
        {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : "💾"} Guardar decisión
      </Button>
    </div>
  );
}




// Iter 82 — Bloque editable observaciones admin + Limpiar con IA + toggle envío
function ObservacionesAdminBlock({ reporte, parada, onChange }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(parada.observaciones_admin || parada.incidencia_descripcion || "");
  const [usarAdmin, setUsarAdmin] = useState(parada.usar_observaciones_admin || false);
  const [busy, setBusy] = useState(false);
  const [iaBusy, setIaBusy] = useState(false);

  const ruta_id = reporte.ruta_id || reporte.rid;
  const idx = reporte.idx;

  const saveText = async () => {
    setBusy(true);
    try {
      await api.patch(`/rutas/${ruta_id}/paradas/${idx}/observaciones-admin`, {
        observaciones_admin: text,
      });
      toast.success("Observaciones admin guardadas");
      setEditing(false);
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally { setBusy(false); }
  };

  const toggleUsar = async (val) => {
    setUsarAdmin(val);
    try {
      await api.patch(`/rutas/${ruta_id}/paradas/${idx}/observaciones-admin`, {
        usar_observaciones_admin: val,
      });
      toast.success(val ? "Se enviará la versión EDITADA al sub-cliente" : "Se enviará la versión ORIGINAL del montador");
      onChange && onChange();
    } catch (e) {
      setUsarAdmin(!val);
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const limpiarConIA = async () => {
    setIaBusy(true);
    try {
      const { data } = await api.post(`/rutas/${ruta_id}/paradas/${idx}/observaciones-limpiar-ia`, {
        texto: parada.incidencia_descripcion || "",
      });
      setText(data.texto_limpio);
      setEditing(true);
      toast.success("Texto limpio sugerido — revísalo y guarda");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "IA no disponible");
    } finally { setIaBusy(false); }
  };

  return (
    <div className="bg-amber-500/10 border-l-4 border-amber-500/60 rounded p-3 text-sm space-y-3"
         data-testid="obs-admin-block">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold">
          Descripción del reporte (original del montador)
        </div>
        <div className="flex gap-1">
          <button onClick={limpiarConIA} disabled={iaBusy}
            data-testid="obs-limpiar-ia-btn"
            className="text-[11px] px-2 py-1 rounded bg-fuchsia-500/15 border border-fuchsia-500/40 text-fuchsia-200 hover:bg-fuchsia-500/25 disabled:opacity-50">
            {iaBusy ? "Limpiando…" : "✨ Limpiar con IA"}
          </button>
          {!editing && (
            <button onClick={() => setEditing(true)}
              data-testid="obs-editar-btn"
              className="text-[11px] px-2 py-1 rounded bg-amber-500/15 border border-amber-500/40 text-amber-200 hover:bg-amber-500/25">
              ✏️ Editar
            </button>
          )}
        </div>
      </div>
      <div className="whitespace-pre-wrap text-zinc-300 text-xs italic border-l-2 border-zinc-700 pl-2">
        {parada.incidencia_descripcion}
      </div>
      {(editing || parada.observaciones_admin) && (
        <div className="space-y-2 pt-2 border-t border-amber-500/20">
          <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold">
            Versión editada (admin DNF)
          </div>
          {editing ? (
            <>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                data-testid="obs-admin-textarea"
                className="w-full bg-zinc-950 border border-emerald-500/40 rounded px-2 py-1.5 text-sm" />
              <div className="flex gap-2">
                <button onClick={saveText} disabled={busy}
                  data-testid="obs-admin-save-btn"
                  className="text-xs px-3 py-1.5 rounded bg-emerald-500 text-zinc-900 font-bold hover:bg-emerald-400 disabled:opacity-50">
                  {busy ? "Guardando…" : "Guardar"}
                </button>
                <button onClick={() => { setText(parada.observaciones_admin || ""); setEditing(false); }}
                  className="text-xs px-3 py-1.5 rounded border border-zinc-700 text-zinc-400">
                  Cancelar
                </button>
              </div>
            </>
          ) : (
            <div className="whitespace-pre-wrap text-zinc-100">{parada.observaciones_admin}</div>
          )}
          {parada.observaciones_admin_editado_por && (
            <div className="text-[10px] text-zinc-500">
              Editado por {parada.observaciones_admin_editado_por} ·{" "}
              {parada.observaciones_admin_editado_at && new Date(parada.observaciones_admin_editado_at).toLocaleString("es-ES")}
            </div>
          )}
        </div>
      )}
      {parada.observaciones_admin && (
        <label className="flex items-center gap-2 text-xs cursor-pointer pt-2 border-t border-amber-500/20">
          <input
            type="checkbox"
            checked={usarAdmin}
            onChange={(e) => toggleUsar(e.target.checked)}
            data-testid="obs-toggle-usar-admin"
            className="w-4 h-4 accent-emerald-500" />
          <span className={usarAdmin ? "text-emerald-300 font-bold" : "text-zinc-400"}>
            Enviar la versión EDITADA al sub-cliente {usarAdmin ? "✓" : "(sin marcar → se envía la original)"}
          </span>
        </label>
      )}
    </div>
  );
}

// Iter 101 — Completar datos de facturación de una parada manual
function DialogCompletarManual({ reporte, onClose, onDone }) {
  const p = reporte.parada;
  const [clientes, setClientes] = useState([]);
  const [busca, setBusca] = useState("");
  const [clienteId, setClienteId] = useState(p.manual_cliente_id || "");
  const [espacios, setEspacios] = useState([]);
  const [espacioId, setEspacioId] = useState(p.manual_espacio_id || "");
  const [concepto, setConcepto] = useState(p.manual_concepto || p.manual_titulo || "");
  const [importe, setImporte] = useState(p.manual_importe ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/clientes").then((r) => setClientes(r.data || [])).catch(() => {});
    // Iter 102 — Fernando (feb/2026): NO filtramos espacios por cliente_id.
    // Muchos clientes actúan como instaladores (Decor Galva monta para muchas
    // marcas cuyos PDVs pertenecen a OTROS clientes en BD). El EspacioCombobox
    // destaca con badge "CLIENTE" los propios y ordena esos primero.
    api.get("/espacios", { params: { limit: 5000 } })
      .then((r) => setEspacios(r.data || []))
      .catch(() => setEspacios([]));
  }, []);

  const filtrados = clientes.filter(
    (c) => !busca.trim() || (c.nombre || "").toLowerCase().includes(busca.trim().toLowerCase())
  );
  const clienteSel = clientes.find((c) => c.id === clienteId);

  const guardar = async () => {
    if (!clienteId) { toast.error("Selecciona un cliente"); return; }
    if (!importe || Number(importe) <= 0) { toast.error("Pon un importe mayor que 0"); return; }
    setSaving(true);
    try {
      await api.patch(`/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/completar-manual`, {
        cliente_id: clienteId,
        espacio_id: espacioId || null,
        concepto: concepto.trim() || null,
        importe: Number(importe),
      });
      toast.success("Datos guardados · ya puedes enviarlo a FD");
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando datos");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sm">✏️ Completar datos de facturación</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-xs text-zinc-400">
            {p.manual_titulo || "Parada manual"} · {reporte.codigo_ruta} #{p.orden}
          </div>
          <div>
            <div className="text-xs text-zinc-500 mb-1">Cliente <span className="text-red-400">*</span></div>
            {clienteSel ? (
              <div className="flex items-center justify-between gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded px-2.5 py-1.5">
                <span className="text-emerald-200 text-xs font-semibold truncate">{clienteSel.nombre}</span>
                <button onClick={() => { setClienteId(""); setBusca(""); setEspacioId(""); }}
                  data-testid="cambiar-cliente-btn"
                  className="text-[10px] text-zinc-400 hover:text-red-300 shrink-0">cambiar</button>
              </div>
            ) : (
              <>
                <Input value={busca} onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar cliente…" data-testid="buscar-cliente-input"
                  className="bg-zinc-900 border-zinc-800 text-sm mb-1" />
                <div className="max-h-40 overflow-y-auto border border-zinc-800 rounded divide-y divide-zinc-800/60"
                  data-testid="lista-clientes">
                  {filtrados.slice(0, 50).map((c) => (
                    <button key={c.id} onClick={() => setClienteId(c.id)}
                      data-testid={`cliente-opcion-${c.id}`}
                      className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-amber-500/10 hover:text-amber-200 transition-colors">
                      {c.nombre}
                    </button>
                  ))}
                  {filtrados.length === 0 && (
                    <div className="px-2.5 py-2 text-xs text-zinc-500 italic">Sin resultados</div>
                  )}
                </div>
              </>
            )}
          </div>
          {clienteId && espacios.length > 0 && (
            <div>
              <div className="text-xs text-zinc-500 mb-1">Espacio (opcional)</div>
              <EspacioCombobox
                value={espacioId}
                onChange={setEspacioId}
                espacios={espacios}
                clienteId={clienteId}
                allowNone
                testId="espacio-combobox"
                placeholder="Buscar espacio…"
              />
            </div>
          )}
          <div>
            <div className="text-xs text-zinc-500 mb-1">Concepto</div>
            <Input value={concepto} onChange={(e) => setConcepto(e.target.value)}
              placeholder="Ej: Montaje vinilo escaparate" data-testid="concepto-input"
              className="bg-zinc-900 border-zinc-800 text-sm" />
          </div>
          <div>
            <div className="text-xs text-zinc-500 mb-1">Importe (sin IVA) <span className="text-red-400">*</span></div>
            <Input type="number" min="0" step="0.01" value={importe}
              onChange={(e) => setImporte(e.target.value)}
              placeholder="0.00" data-testid="importe-input"
              className="bg-zinc-900 border-zinc-800 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <Button variant="outline" onClick={onClose}
              className="bg-transparent border-zinc-700 text-zinc-400">Cancelar</Button>
            <Button onClick={guardar} disabled={saving}
              data-testid="guardar-manual-btn"
              className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Jun-2026: fecha real del montaje editable (cuando el reporte se subió otro día)
function FechaMontajeEditable({ reporte, p, onChange }) {
  const efectiva = p.fecha_montaje_real || p.finalizado_at || reporte.fecha;
  const soloFecha = (efectiva || "").slice(0, 10);
  const [editando, setEditando] = useState(false);
  const [val, setVal] = useState(soloFecha);
  const [saving, setSaving] = useState(false);

  const guardar = async () => {
    if (!val) return;
    setSaving(true);
    try {
      await api.patch(`/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/fecha-montaje`, { fecha: val });
      toast.success("Fecha de montaje actualizada");
      setEditando(false);
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error actualizando la fecha");
    } finally { setSaving(false); }
  };

  if (editando) {
    return (
      <div className="flex items-center gap-1.5 justify-end" data-testid={`fecha-montaje-edit-${reporte.ruta_id}-${reporte.idx}`}>
        <input type="date" value={val} onChange={(e) => setVal(e.target.value)}
          data-testid="fecha-montaje-input"
          className="bg-zinc-900 border border-zinc-700 rounded px-1.5 py-0.5 text-[11px] text-zinc-200" />
        <button onClick={guardar} disabled={saving} data-testid="fecha-montaje-guardar"
          className="text-emerald-300 hover:text-emerald-200 font-semibold disabled:opacity-50 text-[11px]">
          {saving ? "…" : "OK"}
        </button>
        <button onClick={() => setEditando(false)} className="text-zinc-500 hover:text-zinc-300 text-[11px]">✕</button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 justify-end" data-testid={`fecha-montaje-${reporte.ruta_id}-${reporte.idx}`}>
      <span>📅 {fechaCorta(soloFecha)}</span>
      {p.fecha_montaje_real && <span className="text-amber-400/70 italic text-[10px]">(corregida)</span>}
      <button onClick={() => { setVal(soloFecha); setEditando(true); }}
        data-testid="fecha-montaje-editar" title="Corregir fecha real del montaje"
        className="text-zinc-500 hover:text-amber-300 transition-colors">
        <Pencil className="w-3 h-3" />
      </button>
    </div>
  );
}
